<template>
    <div class="calendar-area card" :class="{ fixed: scrollPosition >= 140 }">
        <div class="row btn-group" style="justify-content: center" role="group">
            <button
                v-for="calendarData in typeof controls === 'undefined'
                    ? ranges
                    : controls"
                :key="calendarData.type"
                type="button"
                class="btn btn-outline-primary range-btn"
                :class="{ active: selectedRange === calendarData.type }"
                @click="handleTypeChange(calendarData.type)"
            >
                {{ calendarData.title }}
            </button>
        </div>
        <div class="row mt-4 mb-2">
            <div class="col-2">
                <button
                    :disabled="!isLeftActive"
                    class="btn btn-outline-secondary btn-nav-range"
                    style="float: right"
                    @click="handleArrowClick(0)"
                >
                    <font-awesome-icon icon="fa-solid fa-arrow-left" />
                </button>
            </div>
            <div class="col-8">
                <h1 class="text-center range-title mb-1 mt-1">
                    {{ title ? title : defaultTitle }}
                    <span class="range-name">{{ rangeText }}</span>
                </h1>
            </div>
            <div class="col-2">
                <button
                    :disabled="!isRightActive"
                    class="btn btn-outline-secondary btn-nav-range"
                    style="float: left"
                    @click="handleArrowClick(1)"
                >
                    <font-awesome-icon icon="fa-solid fa-arrow-right" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";

export default {
  name: "Calendar",
  props: {
    eventName: String,
    controls: Array,
    title: String
  },
  data() {
    return {
      defaultTitle: "Analytics for",
      scrollPosition: null,
      startDate: null,
      endDate: null,
      isRightActive: true,
      isLeftActive: true,
      selectedRange: 3,
      lastDayOfMonth: null,
      day: null,
      month: null,
      year: null,
      unit: null,
      rangeText: "Title for ranges",
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ],
      ranges: [
        { title: "Year (by quarter)", type: 1 },
        { title: "Year (by month)", type: 2 },
        { title: "Month", type: 3 },
        { title: "Week", type: 4 },
        { title: "Day", type: 5 }
      ]
    };
  },
  mounted() {
    this.handleTypeChange(this.selectedRange);
    this.triggerSearch();

    window.addEventListener("scroll", this.updateScroll);
  },
  methods: {
    updateScroll() {
      this.scrollPosition = window.scrollY;
    },
    /**
     * Handle ranges clicks and set initial values
     */
    handleTypeChange(type) {
      this.selectedRange = type;
      this.isRightActive = true;
      this.isLeftActive = true;

      let today = new Date();

      switch (this.selectedRange) {
        case 1:
        case 2:
          this.day = 1;
          this.month = today.getMonth();
          this.year = today.getFullYear();

          if (this.year >= today.getFullYear()) {
            this.isRightActive = false;
          }
          break;
        case 3:
          this.day = 1;
          this.month = today.getMonth();
          this.year = today.getFullYear();

          if (
            this.month >= today.getMonth() &&
            this.year >= today.getFullYear()
          ) {
            this.isRightActive = false;
          }

          break;
        case 4:
          let first = today.getDate() - today.getDay();
          let last = first + 6;

          this.startDate = new Date(today.setDate(first));
          this.endDate = new Date(today.setDate(last));

          if (this.endDate.getDate() >= today.getDate()) {
            this.isRightActive = false;
          }
          break;
        case 5:
          this.day = today.getDate();
          this.month = today.getMonth();
          this.year = today.getFullYear();
          this.lastDayOfMonth = new Date(
            this.year,
            this.month + 1,
            0
          ).getDate();

          if (
            this.day >= today.getDate() &&
            this.month >= today.getMonth() &&
            this.year >= today.getFullYear()
          ) {
            this.isRightActive = false;
          }

          break;
      }
      this.updateRangeText();
      this.setDates();
      this.triggerSearch();
    },
    /**
     * Handle arrows clicks and update ranges
     *
     * @param arrowType
     */
    handleArrowClick(arrowType) {
      this.isRightActive = true;
      this.isLeftActive = true;

      let today = new Date();

      switch (this.selectedRange) {
        case 1:
        case 2:
          this.year = arrowType === 0 ? this.year - 1 : this.year + 1;

          if (this.year >= today.getFullYear()) {
            this.isRightActive = false;
          }
          break;
        case 3:
          this.month = arrowType === 0 ? this.month - 1 : this.month + 1;

          if (this.month > 11) {
            this.month = 0;
            this.year++;
          }

          if (this.month < 0) {
            this.month = 11;
            this.year--;
          }

          if (
            this.month >= today.getMonth() &&
            this.year >= today.getFullYear()
          ) {
            this.isRightActive = false;
          }
          break;
        case 4:
          if (arrowType > 0) {
            let date = this.endDate;

            this.startDate = new Date(date.setDate(date.getDate() + 1));
            this.endDate = new Date(date.setDate(date.getDate() + 6));
          }

          if (arrowType === 0) {
            let date = this.startDate;

            this.endDate = new Date(date.setDate(date.getDate() - 1));
            this.startDate = new Date(date.setDate(date.getDate() - 6));
          }

          if (this.endDate.getDate() >= today.getDate()) {
            this.isRightActive = false;
          }
          break;
        case 5:
          this.day = arrowType === 0 ? this.day - 1 : this.day + 1;

          if (
            this.day >= today.getDate() &&
            this.month >= today.getMonth() &&
            this.year >= today.getFullYear()
          ) {
            this.isRightActive = false;
          }

          if (this.day > this.lastDayOfMonth) {
            this.day = 1;
            this.month++;

            if (this.month > 11) {
              this.year++;
              this.month = 0;
            }
          }

          if (this.day < 1) {
            this.month--;

            if (this.month < 0) {
              this.month = 11;
              this.year--;
            }

            this.lastDayOfMonth = new Date(
              this.year,
              this.month + 1,
              0
            ).getDate();
            this.day = this.lastDayOfMonth;
          }

          break;
      }

      this.updateRangeText();
      this.setDates();
      this.triggerSearch();
    },
    /**
     * Update range texts
     */
    updateRangeText() {
      switch (this.selectedRange) {
        case 1:
          this.rangeText = this.year;
          break;
        case 2:
          this.rangeText = this.year;
          break;
        case 3:
          this.rangeText = this.months[this.month] + " " + this.year;
          break;
        case 4:
          this.rangeText =
            this.startDate.getDate() +
            " " +
            this.months[this.startDate.getMonth()] +
            " " +
            this.startDate.getFullYear() +
            " - " +
            this.endDate.getDate() +
            " " +
            this.months[this.endDate.getMonth()] +
            " " +
            this.endDate.getFullYear();
          break;
        case 5:
          this.rangeText =
            this.day + " " + this.months[this.month] + " " + this.year;
          break;
      }
    },
    setDates() {
      switch (this.selectedRange) {
        case 1:
          this.startDate = new Date(this.year, 0, 1);
          this.endDate = new Date(this.year, 12, 0);
          this.unit = "quarter";
          break;
        case 2:
          this.startDate = new Date(this.year, 0, 1);
          this.endDate = new Date(this.year, 12, 0);
          this.unit = "year";
          break;
        case 3:
          this.startDate = new Date(this.year, this.month, 1);
          this.endDate = new Date(this.year, this.month + 1, 0);
          this.unit = "month";
          break;
        case 4:
          // The dates were set in the method above
          this.unit = "week";
          break;
        case 5:
          this.startDate = this.endDate = new Date(
            this.year,
            this.month,
            this.day
          );
          this.unit = "day";
          break;
      }
    },
    triggerSearch() {
      this.$emit(this.eventName ? this.eventName : "date-changed", {
        startDate: moment(this.startDate).format("YYYY-MM-DD"),
        endDate: moment(this.endDate).format("YYYY-MM-DD"),
        unit: this.unit,
        rangeTitle: this.rangeText
      });
    }
  }
};
</script>

<style scoped>
.calendar-area {
  text-align: center;
}

.btn-nav-range {
  padding: 7px 7px 7px 13px;
  border-radius: 50%;
}

.range-title {
  font-size: 23px;
}

.btn-group {
  display: flex;
}

.range-btn {
  max-width: 20%;
  padding: 5px;
}

.btn-group > .btn:first-child {
  margin-left: 15px;
}

.btn-group > .btn:last-child {
  margin-right: 15px;
}

.range-name {
  color: #adafb1;
}

.fixed {
  position: sticky;
  top: 54px;
  z-index: 999;
  background: white;
  height: min-content;
  box-shadow: 1px 2px 9px 0px rgb(34 60 80 / 20%);
  padding-bottom: 5px;
}
</style>
